/* Hallmark · component: Input · genre: modern-minimal · theme: maeiz-tokens
 * states: default · hover · focus · active · disabled · error
 * height: h-11 (matches Button md)
 */
import React from "react";
import { clsx } from "clsx";
import { CONTROL_HEIGHT, fieldAddonClass, fieldIconClass, fieldSurfaceClass } from "../../styles/field";

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size" | "prefix"> {
    label?: string;
    error?: string;
    helperText?: string;
    fullWidth?: boolean;
    /** Prefix icon/node inside the field */
    prefix?: React.ReactNode;
    /** Suffix icon/node inside the field */
    suffix?: React.ReactNode;
    /** @deprecated Prefer `prefix` */
    leftIcon?: React.ReactNode;
    /** @deprecated Prefer `suffix` */
    rightIcon?: React.ReactNode;
    /** Text addon before the field (e.g. +92, https://) */
    prefixAddon?: React.ReactNode;
    /** Text addon after the field (e.g. PKR, .com) */
    suffixAddon?: React.ReactNode;
    size?: "sm" | "md" | "lg";
    labelClassName?: string;
    inputTextClassName?: string;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
    (
        {
            label,
            error,
            helperText,
            fullWidth = false,
            prefix,
            suffix,
            leftIcon,
            rightIcon,
            prefixAddon,
            suffixAddon,
            size = "md",
            className,
            id,
            labelClassName,
            inputTextClassName,
            disabled,
            ...props
        },
        ref,
    ) => {
        const inputId = id || `input-${Math.random().toString(36).slice(2, 9)}`;
        const hasError = !!error;
        const startIcon = prefix ?? leftIcon;
        const endIcon = suffix ?? rightIcon;
        const height = CONTROL_HEIGHT[size];
        const hasAddons = !!(prefixAddon || suffixAddon);

        const control = (
            <div className={clsx("relative min-w-0 flex-1", hasAddons && "flex")}>
                {startIcon ? (
                    <div className={clsx(fieldIconClass, "left-3")} aria-hidden>
                        {startIcon}
                    </div>
                ) : null}
                <input
                    id={inputId}
                    ref={ref}
                    disabled={disabled}
                    className={clsx(
                        fieldSurfaceClass({ error: hasError, disabled }),
                        height,
                        startIcon ? "pl-10" : "pl-4",
                        endIcon ? "pr-10" : "pr-4",
                        hasAddons && "rounded-none",
                        prefixAddon && !suffixAddon && "rounded-e-xl",
                        suffixAddon && !prefixAddon && "rounded-s-xl",
                        !hasAddons && "rounded-xl",
                        inputTextClassName,
                        className,
                    )}
                    aria-invalid={hasError}
                    aria-describedby={error ? `${inputId}-error` : helperText ? `${inputId}-helper` : undefined}
                    {...props}
                />
                {endIcon ? (
                    <div className={clsx(fieldIconClass, "pointer-events-auto right-3")}>{endIcon}</div>
                ) : null}
            </div>
        );

        return (
            <div className={clsx("flex flex-col gap-1.5", fullWidth && "w-full")}>
                {label ? (
                    <label
                        htmlFor={inputId}
                        className={clsx("text-sm font-medium text-neutral-dark dark:text-white", labelClassName)}
                    >
                        {label}
                    </label>
                ) : null}

                {hasAddons ? (
                    <div className="flex w-full min-w-0 items-stretch">
                        {prefixAddon ? (
                            <span className={clsx(fieldAddonClass, height, "rounded-s-xl border-e-0")}>{prefixAddon}</span>
                        ) : null}
                        {control}
                        {suffixAddon ? (
                            <span className={clsx(fieldAddonClass, height, "rounded-e-xl border-s-0")}>{suffixAddon}</span>
                        ) : null}
                    </div>
                ) : (
                    control
                )}

                {error ? (
                    <p id={`${inputId}-error`} className="text-sm text-danger" role="alert">
                        {error}
                    </p>
                ) : null}
                {helperText && !error ? (
                    <p id={`${inputId}-helper`} className="text-sm text-neutral-secondary">
                        {helperText}
                    </p>
                ) : null}
            </div>
        );
    },
);

Input.displayName = "Input";
